import type { GetStaticPaths, NextPage } from "next";
import dynamic from "next/dynamic";
import {
  sanityBlogOverviewDocumentId,
  sanityCaseStudiesOverviewDocumentId,
  sanityWebinarsOverviewDocumentId,
  stripSanityGroqFunctions,
  type SanitySiteSettingsNavigation,
} from "@ouster/shared";

import { SanityRouteView, type SerializedSanityRoute } from "~/features/sanity/route-view";
import { loadSanityBlogPageData, loadSanityBlogPageLive } from "~/features/sanity/blog/loader.server";
import type { SanityBlogPageData } from "~/features/sanity/blog/types";
import {
  loadSanityCaseStudiesPageData,
  loadSanityCaseStudiesPageLive,
} from "~/features/sanity/case-studies/loader.server";
import type { SanityCaseStudiesPageData } from "~/features/sanity/case-studies/types";
import { loadSanityWebinarsPageData } from "~/features/sanity/webinars/loader.server";
import type { SanityWebinarsPageData } from "~/features/sanity/webinars/types";
import { toSanityLiveQuery } from "~/features/sanity/loader.server";
import type { SanityLivePageData, SanityRouteHeroSlice } from "~/features/sanity/live/types";
import type { SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { serializeSection } from "~/features/sanity/page-builder/serialize";
import { sanityRouteHeroSliceQuery, toSanityLiveSiteSettings } from "~/features/sanity/routes";
import { SSGSanityPageContextHandler, type ExtractSanityPageProps } from "~/features/sanity/page-context.server";

// Preview-only live view. Code-split via next/dynamic so the live subscription
// layer (usePresentationQuery + @sanity/visual-editing) never ships to normal
// visitors (it is rendered only when `props.live` is present, i.e. under
// Sanity draft mode).
const LiveSanityRouteView = dynamic(() =>
  import("~/features/sanity/live/route-view").then((mod) => mod.LiveSanityRouteView)
);

type RouteParams = {
  uri?: string[];
};

type PageProps = {
  mode?: "default" | "webinarWatch";
  route: SerializedSanityRoute;
  routeUri: string;
  sections: SanityPageBuilderRenderSection[];
  siteSettings: SanitySiteSettingsNavigation | null;
  blogData: SanityBlogPageData | null;
  caseStudiesData: SanityCaseStudiesPageData | null;
  webinarsData: SanityWebinarsPageData | null;
  /** Live-subscription bundle; present only under preview, else `null`. */
  live: SanityLivePageData | null;
};

function getRouteUri(uri?: string[]) {
  return uri?.length ? uri.join("/") : "/";
}

function getWebinarWatchParentUri(uri: string) {
  if (uri === "/" || !uri.endsWith("/watch")) {
    return null;
  }

  return uri.slice(0, -"/watch".length);
}

/** Round-trip through JSON to drop `undefined` (e.g. absent source maps), which
 * `getStaticProps` cannot serialize. Preview-only, so the cost is acceptable. */
function stripUndefined<T>(value: T): T {
  return JSON.parse(JSON.stringify(value ?? null));
}

// All routable documents are resolved on-demand by URI; nothing is prebuilt.
export const getStaticPaths: GetStaticPaths<RouteParams> = async () => ({
  fallback: "blocking",
  paths: [],
});

export const getStaticProps = SSGSanityPageContextHandler<PageProps, RouteParams>(
  async (ctx) => {
    const routeUri = getRouteUri(ctx.params?.uri);
    const watchParentUri = getWebinarWatchParentUri(routeUri);
    const exactRouteResult = await ctx.sanity.getRouteDocumentByUri(routeUri);
    let route = exactRouteResult.res.result;
    let routeResult = exactRouteResult;
    let mode: PageProps["mode"] = "default";

    if (!route?._id && watchParentUri) {
      const parentRouteResult = await ctx.sanity.getRouteDocumentByUri(watchParentUri);
      route = parentRouteResult.res.result;
      routeResult = parentRouteResult;

      if (route?._id && route._type === "webinar" && route.webinarMetadata?.watchMedia) {
        mode = "webinarWatch";
      }
    }

    if (!route?._id) {
      return { notFound: true };
    }

    if (watchParentUri && mode !== "webinarWatch" && !exactRouteResult.res.result?._id) {
      return { notFound: true };
    }

    const { pageBuilder: _pageBuilder, ...serializedRoute } = route;
    const loadedSections =
      mode === "webinarWatch" ? [] : await ctx.sanity.loadPageBuilderSections({ docId: route._id });
    const isPreview = ctx.sanity.isPreview;

    // The Blog overview renders a listing layout instead of the plain hero +
    // sections flow, so it needs the article/filter data loaded server-side.
    const isBlogOverview = mode === "default" && route._id === sanityBlogOverviewDocumentId;
    let blogData: SanityBlogPageData | null = null;
    let blogLive: SanityLivePageData["blog"] = null;
    if (isBlogOverview) {
      if (isPreview) {
        const blog = await loadSanityBlogPageLive(ctx.sanity.fetchOptions);
        blogData = blog.blogData;
        blogLive = blog.live;
      } else {
        blogData = await loadSanityBlogPageData(ctx.sanity.fetchOptions);
      }
    }

    const isCaseStudiesOverview = mode === "default" && route._id === sanityCaseStudiesOverviewDocumentId;
    let caseStudiesData: SanityCaseStudiesPageData | null = null;
    let caseStudiesLive: SanityLivePageData["caseStudies"] = null;
    if (isCaseStudiesOverview) {
      if (isPreview) {
        const caseStudies = await loadSanityCaseStudiesPageLive(ctx.sanity.fetchOptions);
        caseStudiesData = caseStudies.caseStudiesData;
        caseStudiesLive = caseStudies.live;
      } else {
        caseStudiesData = await loadSanityCaseStudiesPageData(ctx.sanity.fetchOptions);
      }
    }

    // The Webinars overview restores the card index the cutover dropped. The
    // grid derives from the `webinar` collection (not the edited overview doc),
    // so it needs no live subscription of its own — the page-builder sections
    // still update live via `PageBuilderComponent`.
    const isWebinarsOverview = mode === "default" && route._id === sanityWebinarsOverviewDocumentId;
    const webinarsData: SanityWebinarsPageData | null = isWebinarsOverview
      ? await loadSanityWebinarsPageData(ctx.sanity.fetchOptions)
      : null;

    const sections = loadedSections.map(serializeSection);
    // Initial hero data is picked from the route fetch (same shared projection
    // fragments), so the dedicated hero subscription needs no extra query here.
    const heroSlice: SanityRouteHeroSlice = {
      _id: route._id,
      _type: route._type,
      title: route.title,
      hero: route.hero,
      hardwareMetadata: route.hardwareMetadata,
      softwareMetadata: route.softwareMetadata,
    };
    const live: SanityLivePageData | null = isPreview
      ? {
          route: toSanityLiveQuery(routeResult),
          hero: {
            // Serialized without the o:: preamble (re-joined in `useSanityLiveQuery`).
            query: stripSanityGroqFunctions(sanityRouteHeroSliceQuery),
            params: { docId: route._id },
            initial: { data: heroSlice, sourceMap: undefined, perspective: "drafts" },
          },
          siteSettings: toSanityLiveSiteSettings(ctx.sanity.siteSettingsSources),
          blog: blogLive,
          caseStudies: caseStudiesLive,
        }
      : null;

    return {
      props: {
        mode,
        route: serializedRoute,
        routeUri,
        // `sections` and `live` carry `initial.sourceMap` that is `undefined` for
        // some subscriptions; strip it so Next can serialize the props (preview only).
        sections: isPreview ? stripUndefined(sections) : sections,
        siteSettings: ctx.siteSettings,
        blogData,
        caseStudiesData,
        webinarsData,
        live: stripUndefined(live),
      },
    };
  },
  {
    getPath: (context) => getRouteUri(context.params?.uri),
  }
);

const CatchAllPage: NextPage<ExtractSanityPageProps<typeof getStaticProps>> = (props) => {
  if (props.live) {
    return (
      <LiveSanityRouteView
        mode={props.mode}
        route={props.route}
        sections={props.sections}
        siteSettings={props.siteSettings}
        blogData={props.blogData}
        caseStudiesData={props.caseStudiesData}
        webinarsData={props.webinarsData}
        live={props.live}
      />
    );
  }

  return (
    <SanityRouteView
      mode={props.mode}
      route={props.route}
      sections={props.sections}
      siteSettings={props.siteSettings}
      blogData={props.blogData}
      caseStudiesData={props.caseStudiesData}
      webinarsData={props.webinarsData}
    />
  );
};

export default CatchAllPage;
